import React, { useState, useContext, useEffect, useMemo } from 'react'
import * as R from 'ramda'

import { SurveyPublishSettingContext } from 'pages/SurveyPublishSettingV2'
import { findMetricQuestion } from 'organisms/SurveySettingsV2/utils'
import { CSATEmbed } from './CSATEmbed'
import { NPSEmbed } from './NPSEmbed'
import { STAREmbed } from './STAREmbed'
import { useParams } from 'react-router'
import useAxios, { mutate } from 'hooks/useAxios'
import { surveySettingContext } from 'pages/SurveySettings/context'
import { toast } from 'libs/toast'
import { isEmbedSetupsDirty } from './utils'

function _getDefaultEmbedStyleType(metric) {
  if (metric === 'CSAT' || metric === '5-STAR') {
    return 'icon'
  }
  return 'basic'
}

/**
 *
 * @param {object} props
 * @param {string} props.primaryQuestionTitle
 * @param {string} props.surveyType
 */
export function EmbedSurveyV2({ primaryQuestionTitle, surveyType }) {
  const { surveyId } = useParams()
  const { data: survey } = useAxios({
    url: `account/survey/${surveyId}/`,
  })
  const { fetch: updateSurvey, loading } = useAxios({
    method: 'PATCH',
    url: `account/survey/${surveyId}/`,
    skip: true,
  })

  const [isRemovePlaceholder, setIsRemovePlaceholder] = useState(false)

  const publishSettings = useContext(SurveyPublishSettingContext)
  const embedIntegrations = publishSettings.embedIntegrations
  const selectedIntegration = getEmbedType(surveyType, embedIntegrations)
  const surveyConfiguration = findSurveyConfiguration(selectedIntegration, embedIntegrations)

  const primaryQuestion =
    findMetricQuestion(publishSettings.survey.questions) || publishSettings.survey.questions[0]
  const [embedStyleType, setEmbedStyleType] = useState(
    publishSettings.styleType || _getDefaultEmbedStyleType(primaryQuestion.metric)
  )

  const showPlaceholderRemover = ['zendesk'].includes(selectedIntegration)
  const hideEmojis = ['autotask', 'autotaskproject'].includes(survey?.type)

  const customField = {
    ticket: surveyConfiguration.ticketCustomFields,
    teamMember: surveyConfiguration.teamMemberCustomFields,
  }

  const genericEmbedPlaceholders = useMemo(
    () => ({
      question: primaryQuestionTitle,
      teamMemberEmail: publishSettings.teamMemberEmail,
      teamMemberId: publishSettings.teamMemberId,
      teamMemberFirstName: publishSettings.teamMemberFirstName,
      teamMemberLastName: publishSettings.teamMemberLastName,
      customerEmail: publishSettings.customerEmail,
      customerId: publishSettings.customerId,
      customerFirstName: publishSettings.customerFirstName,
      customerLastName: publishSettings.customerLastName,
      companyName: publishSettings.companyName,
      ticketId: publishSettings.ticketId,
      ticketSubject: publishSettings.ticketSubject,
    }),
    [publishSettings, primaryQuestionTitle]
  )

  const embedSurveySetups = useMemo(
    () => ({
      styleType: embedStyleType,
      isShowLabel: publishSettings.isShowLabel,
      isDisplayColor: publishSettings.isDisplayColor,
      choiceOrder: publishSettings.choiceOrder,
      ratingIconSize: publishSettings.ratingIconSize,
    }),
    [publishSettings, embedStyleType]
  )

  const ticketGroup = publishSettings.ticketGroup
  const { setContext } = useContext(surveySettingContext)

  // PET-2084: feed the survey-settings unsaved-changes guard. Baseline-compare
  // the current placeholders/setups against the saved survey.settings (rather
  // than flipping a flag inside the keystroke handlers, which re-rendered the
  // controlled placeholder inputs in SurveyPublishSettingV2 and broke editing).
  // Reset is automatic: a successful save mutates survey.settings, so
  // current === saved → dirty clears.
  useEffect(() => {
    const savedPlaceholders = R.pathOr({}, ['settings', 'placeholders'], survey)
    const savedGroup = R.pathOr('', ['settings', 'ticketCustomFields', 'group'], survey)
    const savedSetups = R.pathOr({}, ['settings', 'setups'], survey)
    const placeholderKeys = [
      'teamMemberEmail',
      'teamMemberId',
      'teamMemberFirstName',
      'teamMemberLastName',
      'customerEmail',
      'customerId',
      'customerFirstName',
      'customerLastName',
      'companyName',
      'ticketId',
      'ticketSubject',
    ]
    const placeholdersDirty =
      placeholderKeys.some(
        (key) => (genericEmbedPlaceholders[key] || '') !== (savedPlaceholders[key] || '')
      ) || (ticketGroup || '') !== (savedGroup || '')
    // styleType defaults on mount when nothing is saved, so compare it against
    // the saved-or-default value (not raw savedSetups) — otherwise a survey with
    // no saved setups reads as dirty on load.
    const savedStyleType =
      savedSetups.styleType || _getDefaultEmbedStyleType(primaryQuestion.metric)
    const setupsDirty = isEmbedSetupsDirty(embedSurveySetups, savedSetups, savedStyleType)
    setContext({ isSurveyBuilderDirty: placeholdersDirty || setupsDirty })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genericEmbedPlaceholders, embedSurveySetups, ticketGroup, survey])

  useEffect(() => {
    setContext({
      currentSaveFunction: () => {
        updateSurvey({
          data: {
            settings: {
              ...survey.settings,
              placeholders: genericEmbedPlaceholders,
              setups: embedSurveySetups,
              ticketCustomFields: {
                group: ticketGroup,
              },
            },
          },
        })
          .then(() => {
            toast.success('Your survey has been updated.')
            mutate(
              `account/survey/${surveyId}/`,
              {
                ...survey,
                settings: {
                  ...survey.settings,
                  placeholders: genericEmbedPlaceholders,
                  setups: embedSurveySetups,
                },
              },
              false
            )
          })
          .catch(() => {
            toast.error('Oops! Something went wrong. Please try again.')
          })
      },
    })
    return () => {
      setContext({ currentSaveFunction: () => {} })
    }
  }, [
    setContext,
    embedSurveySetups,
    genericEmbedPlaceholders,
    survey.settings,
    ticketGroup,
    updateSurvey,
    survey,
    surveyId,
  ])

  useSetHeaderSaveButtonLoading(loading)

  if (selectedIntegration === 'generic_embed') {
    const genericEmbedCustomField = R.merge(customField, {
      ticket: {
        group: publishSettings.ticketGroup,
      },
    })

    const updateGenericEmbedPlaceholdersAndCustomFields = () =>
      updateSurvey({
        data: {
          settings: {
            ...survey.settings,
            placeholders: genericEmbedPlaceholders,
            setups: embedSurveySetups,
            ticketCustomFields: {
              group: ticketGroup,
            },
          },
        },
      }).catch((error) => {
        console.error(error)
      })

    if (primaryQuestion.metric === 'CSAT') {
      return (
        <CSATEmbed
          customField={genericEmbedCustomField}
          embedStyleType={embedStyleType}
          isRemovePlaceholder={isRemovePlaceholder}
          onUpdateGenericEmbedPlaceholdersAndCustomFields={
            updateGenericEmbedPlaceholdersAndCustomFields
          }
          placeholders={genericEmbedPlaceholders}
          primaryQuestion={primaryQuestion}
          publishSettings={publishSettings}
          selectedIntegration={selectedIntegration}
          setEmbedStyleType={setEmbedStyleType}
          setIsRemovePlaceholder={setIsRemovePlaceholder}
          showPlaceholderRemover={showPlaceholderRemover}
          surveyConfiguration={surveyConfiguration}
          hideEmojis={hideEmojis}
        />
      )
    } else if (primaryQuestion.metric === '5-STAR') {
      return (
        <STAREmbed
          customField={genericEmbedCustomField}
          embedStyleType={embedStyleType}
          isRemovePlaceholder={isRemovePlaceholder}
          onUpdateGenericEmbedPlaceholdersAndCustomFields={
            updateGenericEmbedPlaceholdersAndCustomFields
          }
          placeholders={genericEmbedPlaceholders}
          primaryQuestion={primaryQuestion}
          publishSettings={publishSettings}
          selectedIntegration={selectedIntegration}
          setEmbedStyleType={setEmbedStyleType}
          setIsRemovePlaceholder={setIsRemovePlaceholder}
          showPlaceholderRemover={showPlaceholderRemover}
          surveyConfiguration={surveyConfiguration}
          hideEmojis={hideEmojis}
        />
      )
    } else {
      // NPS, CES
      return (
        <NPSEmbed
          primaryQuestion={primaryQuestion}
          publishSettings={publishSettings}
          selectedIntegration={selectedIntegration}
          surveyConfiguration={surveyConfiguration}
          embedStyleType={embedStyleType}
          setEmbedStyleType={setEmbedStyleType}
          showPlaceholderRemover={showPlaceholderRemover}
          isRemovePlaceholder={isRemovePlaceholder}
          setIsRemovePlaceholder={setIsRemovePlaceholder}
          placeholders={genericEmbedPlaceholders}
          onUpdateGenericEmbedPlaceholdersAndCustomFields={
            updateGenericEmbedPlaceholdersAndCustomFields
          }
          customField={genericEmbedCustomField}
        />
      )
    }
  } else {
    const placeholders = {
      question: primaryQuestionTitle,
      teamMemberEmail: surveyConfiguration.embedPlaceholders.teamMemberEmail,
      teamMemberId: surveyConfiguration.embedPlaceholders.teamMemberId,
      teamMemberFirstName: surveyConfiguration.embedPlaceholders.teamMemberFirstName,
      teamMemberLastName: surveyConfiguration.embedPlaceholders.teamMemberLastName,
      customerEmail: surveyConfiguration.embedPlaceholders.customerEmail,
      customerId: surveyConfiguration.embedPlaceholders.customerId,
      customerFirstName: surveyConfiguration.embedPlaceholders.customerFirstName,
      customerLastName: surveyConfiguration.embedPlaceholders.customerLastName,
      companyName: surveyConfiguration.embedPlaceholders.companyName,
      ticketId: surveyConfiguration.embedPlaceholders.ticketId,
      ticketSubject: surveyConfiguration.embedPlaceholders.ticketSubject,
    }

    if (primaryQuestion.metric === 'CSAT') {
      return (
        <CSATEmbed
          primaryQuestion={primaryQuestion}
          publishSettings={publishSettings}
          selectedIntegration={selectedIntegration}
          surveyConfiguration={surveyConfiguration}
          embedStyleType={embedStyleType}
          setEmbedStyleType={setEmbedStyleType}
          showPlaceholderRemover={showPlaceholderRemover}
          isRemovePlaceholder={isRemovePlaceholder}
          setIsRemovePlaceholder={setIsRemovePlaceholder}
          placeholders={placeholders}
          customField={customField}
          hideEmojis={hideEmojis}
        />
      )
    } else if (primaryQuestion.metric === '5-STAR') {
      return (
        <STAREmbed
          primaryQuestion={primaryQuestion}
          publishSettings={publishSettings}
          selectedIntegration={selectedIntegration}
          surveyConfiguration={surveyConfiguration}
          embedStyleType={embedStyleType}
          setEmbedStyleType={setEmbedStyleType}
          showPlaceholderRemover={showPlaceholderRemover}
          isRemovePlaceholder={isRemovePlaceholder}
          setIsRemovePlaceholder={setIsRemovePlaceholder}
          placeholders={placeholders}
          customField={customField}
          hideEmojis={hideEmojis}
        />
      )
    } else {
      // NPS, CES
      return (
        <NPSEmbed
          primaryQuestion={primaryQuestion}
          publishSettings={publishSettings}
          selectedIntegration={selectedIntegration}
          surveyConfiguration={surveyConfiguration}
          embedStyleType={embedStyleType}
          setEmbedStyleType={setEmbedStyleType}
          showPlaceholderRemover={showPlaceholderRemover}
          isRemovePlaceholder={isRemovePlaceholder}
          setIsRemovePlaceholder={setIsRemovePlaceholder}
          placeholders={placeholders}
          customField={customField}
        />
      )
    }
  }
}

function getEmbedType(surveyType, embedIntegrations) {
  const defaultEmbedType = 'generic_embed'

  return R.compose(
    R.defaultTo(defaultEmbedType),
    R.prop('name'),
    R.find(R.propEq('name', surveyType))
  )(embedIntegrations)
}

function findSurveyConfiguration(integrationType, embedIntegrations) {
  return R.find(R.propEq('name', integrationType))(embedIntegrations)
}

function useSetHeaderSaveButtonLoading(loading) {
  const { setContext } = useContext(surveySettingContext)
  useEffect(() => {
    setContext({
      isSubmitting: loading,
    })

    return () => {
      setContext({
        isSubmitting: false,
      })
    }
  }, [loading, setContext])
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/EmbedSurveyV2/index.js